import {daysUppercase} from "../constants/schedule/days";
import i18n from '../i18n';

export const transformSemesterDate = (date) => {
    if (!date) return new Date(NaN);
    const [day, month, year] = date.split('/');
    return new Date(+year, +month - 1, +day);
};

export const toShortLocalDate = (dateStr, showYear = true) => {
    if (!dateStr) return '';
    const date = transformSemesterDate(dateStr);
    return date.toLocaleDateString(i18n.language, {
        day: 'numeric',
        month: 'short',
        ...(showYear && { year: 'numeric' }),
    });
};

export function isWeekOdd(num) {
    return num % 2 === 1;
}

export const getWeekParity = (startDate, currentDate = new Date()) => {
    const semesterStart = new Date(startDate instanceof Date ? startDate : transformSemesterDate(startDate));
    const targetDate = new Date(currentDate instanceof Date ? currentDate : transformSemesterDate(currentDate));

    semesterStart.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    if (targetDate < semesterStart) return 0;

    const startDay = semesterStart.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat

    // Find the first Monday AFTER the first week
    // If semester starts on Monday - next boundary is 7 days later
    // If semester starts on other day - boundary is next Monday
    const firstWeekEnd = new Date(semesterStart);
    const daysUntilNextMonday = startDay === 0 ? 1 : startDay === 1 ? 7 : 8 - startDay;
    firstWeekEnd.setDate(semesterStart.getDate() + daysUntilNextMonday);

    if (targetDate < firstWeekEnd) return 1;

    const diffTime = targetDate - firstWeekEnd;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const additionalWeeks = Math.floor(diffDays / 7) + 1;

    return additionalWeeks + 1;
};
export const checkSemesterEnd = (semesterEndDate) => {
    const today = new Date();
    const endDate = transformSemesterDate(semesterEndDate);
    return today - endDate > 0;
};

// Returns a function that maps a week key (odd/even) for a given semester
// relative to the reference semester's parity.
// If parities match — returns the original key unchanged.
// If parities differ — swaps odd↔even.
export const getWeekKeyForSemester = (semester, referenceSemester, currentDate = new Date()) => (originalKey) => {
    const semesterWeekIsOdd = getWeekParity(semester.startDay, currentDate) % 2 === 1;
    const referenceWeekIsOdd = getWeekParity(referenceSemester.semester.startDay, currentDate) % 2 === 1;
    const isSynced = semesterWeekIsOdd === referenceWeekIsOdd;
    if (isSynced) return originalKey;
    return originalKey === 'odd' ? 'even' : 'odd';
};

// Returns the reference semester — the one that started earliest
export const getReferenceSemester = (schedules) => {
    return schedules.reduce((earliest, s) =>
            transformSemesterDate(s.semester.startDay) < transformSemesterDate(earliest.semester.startDay)
                ? s
                : earliest
        , schedules[0]);
};

export const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
};

export const formatDate = (date) => {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
};

export const formatTime = (date) => {
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
};

export const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export const addMinutes = (date, minutes) => {
    const result = new Date(date);
    result.setMinutes(result.getMinutes() + minutes);
    return result;
};

export const getDayName = (date) => {
    const jsDay = date.getDay();
    return jsDay === 0 ? daysUppercase[6] : daysUppercase[jsDay - 1];
};

export const formatDayMonth = (date) => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}`;
};

export const generateCalendarDays = (startDay, endDay) => {
    const end = transformSemesterDate(endDay);
    const days = [];
    const current = transformSemesterDate(startDay);

    while (current <= end) {
        days.push({
            date: new Date(current),
            dateFormatted: formatDayMonth(current),
            dayName: getDayName(current),
        });
        current.setDate(current.getDate() + 1);
    }
    return days;
};

// Every date in the semester that falls on the given weekday and belongs to a week of
// the requested parity. 'WEEKLY' returns them all.
export const datesForDayAndParity = (startDay, endDay, dayName, week) => {
    const end = transformSemesterDate(endDay);
    const current = transformSemesterDate(startDay);
    const dates = [];

    while (current <= end) {
        if (getDayName(current) === dayName) {
            const isOdd = isWeekOdd(getWeekParity(startDay, current));
            if (week === 'WEEKLY' || isOdd === (week === 'ODD')) {
                dates.push(new Date(current));
            }
        }
        current.setDate(current.getDate() + 1);
    }
    return dates;
};

// The weekday of an ISO date as the backend names it. Parsed as parts rather than by Date, whose
// string constructor reads a bare yyyy-MM-dd as UTC and can land on the previous day west of it.
export const dayNameOfIsoDate = (isoDate) => {
    const [year, month, day] = isoDate.split('-').map(Number);
    return getDayName(new Date(year, month - 1, day));
};

// How a sidebar row is addressed by the hover highlight. A term names its rows by weekday; a
// session names them by date, since the same weekday comes round more than once. The prefix keeps
// it a valid id and selector — a bare date starts with a digit, which querySelector rejects.
export const scheduleRowKey = (dayName, isoDate) => (isoDate ? `d${isoDate}` : dayName);

// yyyy-MM-dd of a local Date, matching the dates the backend sends. Built from the local parts
// rather than toISOString, which converts to UTC first and can shift the day.
export const toIsoDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

// dd.MM of an ISO date, for column headers.
export const formatIsoDayMonth = (isoDate) => {
    const [, month, day] = isoDate.split('-');
    return `${day}.${month}`;
};

export const isShortSemester = (startDay, endDay) => {
    const start = transformSemesterDate(startDay);
    const end = transformSemesterDate(endDay);
    return Math.round((end - start) / (1000 * 60 * 60 * 24)) <= 14;
};
